import { useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Link from "@/v2-backoffice-client/components/link"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import PortfolioCustomerListDialog from "@/v2-backoffice-client/portfolio/PortfolioCustomerListDialog"
import type { ColumnMetaMap } from "@/v2-backoffice-client/components/table"
import {
  createCustomerColumns,
  customerColumns,
  type PortfolioCustomerRow,
} from "@/v2-backoffice-client/portfolio/portfolio.customerTable.columns"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  PORTFOLIO_SEGMENT_ACTIONS,
  PORTFOLIO_SEGMENT_COLORS,
  PORTFOLIO_SEGMENT_LABELS,
  type PortfolioSegmentKey,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import { derivePortfolioSegments } from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import {
  buildCustomerActivityLink,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioSegmentsProps = ComponentProps<"section"> & {
  customers: PortfolioCustomerSummary[]
}

const TILE_ACCOUNT_LIMIT = 4
const CUSTOMER = customerColumns<PortfolioCustomerRow>()

/** The segment dialog: the account, its churn read, seats, score, trend, money. */
const SEGMENT_COLUMN_METAS: ColumnMetaMap<PortfolioCustomerRow> = {
  name: CUSTOMER.name,
  churn: CUSTOMER.churn,
  seats: CUSTOMER.seats,
  score: CUSTOMER.score,
  trend: CUSTOMER.trend,
  billed: CUSTOMER.billed,
}
const SEGMENT_COLUMNS =
  createCustomerColumns<PortfolioCustomerRow>(SEGMENT_COLUMN_METAS)

// The book of business as four working segments — engagement crossed with
// churn risk, but phrased as the CONVERSATION each combination calls for
// instead of a statistical chart. Tile = segment, biggest accounts first;
// "+N more" opens the full searchable list.
function PortfolioSegments(props: PortfolioSegmentsProps) {
  const { customers, className, ...sectionProps } = props

  const [openSegment, setOpenSegment] = useState<PortfolioSegmentKey | null>(
    null,
  )

  const segments = derivePortfolioSegments(customers)
  const currency =
    customers.find((customer) => customer.primaryCurrency != null)
      ?.primaryCurrency ?? null
  const segmentedCount = segments.reduce(
    (sum, segment) => sum + segment.customers.length,
    0,
  )
  const unsegmentedCount = customers.length - segmentedCount
  const dialogSegment = segments.find((segment) => segment.key === openSegment)

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Segments"
        title="Working segments"
        lead="Churn risk crossed with engagement, phrased as the move each combination calls for."
        info="Churn risk crossed with engagement (both over the last 28 days), phrased as the move each combination calls for. Accounts without a churn baseline aren't segmented yet; names open the account's Activity tab."
      >
        {unsegmentedCount > 0 && (
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            {formatNumber(unsegmentedCount)} accounts without baseline not
            segmented
          </span>
        )}
      </PortfolioSectionHeader>

      <div className="grid flex-1 gap-3 sm:grid-cols-2">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="flex flex-col gap-1.5 rounded-lg border border-l-2 bg-card p-4"
            style={{ borderLeftColor: PORTFOLIO_SEGMENT_COLORS[segment.key] }}
          >
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold">
                {PORTFOLIO_SEGMENT_LABELS[segment.key]}
              </span>
              <span className="ml-auto text-lg font-semibold tabular-nums">
                {formatNumber(segment.customers.length)}
                <span className="ml-1 text-xs font-normal text-muted-foreground">
                  accounts
                </span>
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {PORTFOLIO_SEGMENT_ACTIONS[segment.key]}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {formatNumber(segment.seatCount)} licensed seats ·{" "}
              {formatNumber(segment.idleSeats)} idle
              {segment.billed12M > 0 && (
                <span title="Billed the last 12 months by the segment's accounts — the money riding on this conversation">
                  {" · "}
                  {formatCompactAmount(segment.billed12M, currency)}
                </span>
              )}
            </span>
            {segment.customers.length > 0 && (
              <span className="mt-0.5 text-xs text-muted-foreground">
                {segment.customers
                  .slice(0, TILE_ACCOUNT_LIMIT)
                  .map((customer, index) => (
                    <span key={customer.externalCustomerId}>
                      {index > 0 && ", "}
                      <Link
                        to={buildCustomerActivityLink(
                          customer.externalCustomerId,
                        )}
                        iconSrc={null}
                        className="text-foreground/90"
                      >
                        {customer.name}
                      </Link>
                    </span>
                  ))}
                {segment.customers.length > TILE_ACCOUNT_LIMIT && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="text-foreground/90 underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground"
                      onClick={() => setOpenSegment(segment.key)}
                    >
                      +
                      {formatNumber(
                        segment.customers.length - TILE_ACCOUNT_LIMIT,
                      )}{" "}
                      more
                    </button>
                  </>
                )}
              </span>
            )}
          </div>
        ))}
      </div>

      {dialogSegment != null && (
        <PortfolioCustomerListDialog
          open
          onOpenChange={(next) => {
            if (!next) setOpenSegment(null)
          }}
          title={`${PORTFOLIO_SEGMENT_LABELS[dialogSegment.key]} — ${formatNumber(dialogSegment.customers.length)} accounts`}
          description={`${PORTFOLIO_SEGMENT_ACTIONS[dialogSegment.key]} Numbers are from the last 28 days of tracked usage; biggest accounts first.`}
          tableId="portfolio-segment-dialog"
          rows={dialogSegment.customers.map((customer) => ({ customer }))}
          columns={SEGMENT_COLUMNS}
          initialSorting={[{ id: "seats", desc: true }]}
        />
      )}
    </section>
  )
}

export default PortfolioSegments
